import { useState } from "react";
import Iconify from "../ui/Iconify";
import { enqueueSnackbar } from "notistack";
import { createFeedback, createFeedbackMedia } from "@/api/feedback";
import { useAuthContext } from "@/context/auth/useAuthContext";
import { useTranslations } from "@/lib/static-intl";

const UserFeedbackModal = ({ onClose }) => {
  const apiT = useTranslations("api");
  const userFeedbackT = useTranslations("user_feedback_modal");
  const { user } = useAuthContext();
  const [loader, setLoader] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    attachments: [],
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);

    setFormData((prev) => ({
      ...prev,
      attachments: files.slice(0, 3),
    }));
  };

  const uploadFeedbackMedia = async (feedbackId, attachments) => {
    try {
      for (const file of attachments) {
        const formData = new FormData();
        formData.append("feedbackId", feedbackId);
        formData.append("file", file);
        formData.append("mediaType", file?.type);
        await createFeedbackMedia(formData);
      }
    } catch (error) {
      enqueueSnackbar(
        error?.response?.data?.message ||
          error?.message ||
          apiT("error message"),
        { variant: "error" }
      );
    }
  };

  const resetForm = () => {
    setFormData({
      title: "",
      description: "",
      attachments: [],
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoader(true);
      const attachments = formData?.attachments;
      const response = await createFeedback({
        userId: user?.userId,
        feedbackTitle: formData?.title,
        feedbackDescription: formData?.description,
        feedbackStatusId: "67a0ba696b78a43fde0a41ed",
      });
      if (response?.id) {
        uploadFeedbackMedia(response?.id, attachments);
        enqueueSnackbar(userFeedbackT("feedback_success"), {
          variant: "success",
        });
        resetForm();
        onClose();
      }
    } catch (error) {
      enqueueSnackbar(
        error?.response?.data?.message ||
          error?.message ||
          apiT("error message"),
        { variant: "error" }
      );
    } finally {
      setLoader(false);
    }
  };

  const handleRemoveAttachment = (index) => {
    setFormData((prev) => ({
      ...prev,
      attachments: prev?.attachments?.filter((_, i) => i !== index),
    }));
  };

  return (
    <div
      onClick={onClose}
      className="w-screen h-screen fixed z-[98] top-0 left-0 bottom-0 right-0 bg-black/50 dark:bg-black/70 flex items-center justify-center"
    >
      <form
        onSubmit={handleSubmit}
        className="w-full md:max-w-[480px] h-full md:max-h-[550px] bg-white dark:bg-dark_bg_grey text-black dark:text-white md:rounded-md flex flex-col overflow-hidden divide-y dark:divide-brown"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="w-full px-5 py-3 flex items-center justify-between">
          <span className="font-medium">
            {userFeedbackT("feedback_modal_heading")}
          </span>
          <button type="button" onClick={onClose}>
            <Iconify icon="material-symbols:close-rounded" />
          </button>
        </div>
        <div className="w-full px-5 py-3 h-full overflow-y-auto thin-scrollbar flex flex-col gap-4">
          <div className="w-full flex flex-col">
            <span>{userFeedbackT("feedback_modal_subheading")}</span>
            <p className="text-black/50 dark:text-white/50 text-sm tracking-tight leading-tight">
              {userFeedbackT("feedback_modal_desc")}
            </p>
          </div>
          <div className="w-full flex flex-col gap-0.5 group">
            <label htmlFor="title" className="text-xs font-medium">
              {userFeedbackT("feedback_title")}
            </label>
            <input
              type="text"
              name="title"
              value={formData?.title}
              onChange={handleChange}
              className="w-full border dark:border-brown rounded outline-none py-1 px-2 caret-red placeholder:text-sm focus:border-red transition"
              placeholder={userFeedbackT("feedback_title_placeholder")}
              required
            />
          </div>
          <div className="w-full flex flex-col gap-0.5 group">
            <div className="w-full flex items-end justify-between">
              <label htmlFor="description" className="text-xs font-medium">
                {userFeedbackT("feedback_details")}
              </label>
              <span className="text-[11px] text-black/60 dark:text-white/60">
                {formData?.description?.length}/500
              </span>
            </div>
            <textarea
              rows={4}
              name="description"
              value={formData?.description}
              onChange={handleChange}
              className="w-full border dark:border-brown rounded outline-none py-1 px-2 caret-red placeholder:text-sm focus:border-red transition resize-none thin-scrollbar"
              placeholder={userFeedbackT("feedback_details_placeholder")}
              required
              maxLength={500}
            />
          </div>
          <div className="w-full flex flex-col gap-2 group">
            <div className="w-full flex items-end justify-between">
              <span className="text-xs font-medium">
                {userFeedbackT("feedback_media_upload")}
              </span>
              <span className="text-[11px] text-black/60 dark:text-white/60">
                {formData?.attachments?.length}/3
              </span>
            </div>
            {formData?.attachments?.length > 0 ? (
              <div className="w-full flex gap-3">
                {formData.attachments.length > 0 && (
                  <div className="w-full flex gap-3">
                    {formData.attachments.map((file, index) => (
                      <div
                        key={index}
                        className="w-24 h-28 rounded overflow-hidden border dark:border-brown relative"
                      >
                        <button
                          type="button"
                          onClick={() => handleRemoveAttachment(index)}
                          className="absolute top-0.5 right-0.5 bg-black/50 w-4 h-4 flex items-center justify-center rounded-full cursor-pointer"
                        >
                          <Iconify
                            icon={"material-symbols:close"}
                            className={"!w-3 !h-3 !text-white"}
                          />
                        </button>
                        {file.type.startsWith("image/") ? (
                          <img
                            src={URL.createObjectURL(file)}
                            alt={`Attachment ${index + 1}`}
                            className="w-full h-full object-contain"
                          />
                        ) : (
                          <video
                            muted
                            autoPlay
                            className="w-full h-full object-contain"
                          >
                            <source
                              src={URL.createObjectURL(file)}
                              type={file.type}
                            />
                          </video>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <label
                htmlFor="attachment"
                className="w-20 h-24 rounded flex flex-col items-center justify-center gap-1 border border-dashed dark:border-white/20 hover:border-red cursor-pointer transition bg-red/5"
              >
                <Iconify
                  icon="fluent:arrow-upload-16-regular"
                  className="!text-red"
                />
                <span className="text-[11px] text-red -mt-1">
                  {userFeedbackT("upload")}
                </span>
                <input
                  type="file"
                  name="attachments"
                  id="attachment"
                  hidden
                  accept="image/*,video/*"
                  multiple
                  onChange={handleFileChange}
                />
              </label>
            )}
          </div>
        </div>
        <div className="fixed z-[99] left-0 bottom-0 right-0 bg-white dark:bg-dark_bg_grey md:static w-full px-5 py-3 flex items-center justify-end gap-3">
          <button
            type="submit"
            disabled={loader}
            className={`font-medium border border-red text-white bg-red rounded p-2 text-sm flex items-center justify-center gap-1 ${
              loader && "cursor-not-allowed opacity-70"
            }`}
          >
            {loader && (
              <Iconify
                icon="line-md:loading-loop"
                className={"!text-inherit"}
              />
            )}
            {userFeedbackT("submit")}
          </button>
          <button
            type="button"
            disabled={loader}
            className={`font-medium border dark:border-brown text-black/50 dark:text-white/50 rounded p-2 text-sm ${
              loader && "cursor-not-allowed opacity-70"
            }`}
            onClick={onClose}
          >
            {userFeedbackT("cancel")}
          </button>
        </div>
      </form>
    </div>
  );
};

export default UserFeedbackModal;
